import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Category, CreateTVShowRequest } from '@/types/model';
import {
  tvShowGenreOptions,
  tvShowClassificationOptions
} from '@/constants';
import { useTranslation, useCategoriesForDropdown } from '@/hooks';
import { Translation } from '@/locales';
import { ContentDetailsSource, contentDetailsService } from '@/services/contentDetailsService';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import MediaAssetsSection from '@/components/ui/MediaAssetsSection';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { Search, Loader2, RefreshCw } from 'lucide-react';
import ImageNotAvailable from '@/assets/image-not-available.png';
import { isDigitalOceanSpacesUrl } from '@/utils/validation.ts';
import { MediaSource } from '@/types/media';
import { mapExternalTVShowGenres } from '@/utils/contentGenreMapping';
import TmdbContentAutocomplete from '@/components/forms/TmdbContentAutocomplete';

const createTVShowSchema = (t: Translation) => z.object({
  title: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(200, t.validation.maxLength(200)),
  spanishTitle: z.string().optional(),
  originalTitle: z.string().optional(),
  imdbId: z.string().optional(),
  tmdbId: z.string().optional(),
  year: z
    .number({ message: t.validation.required })
    .min(1900, 'El año debe ser mayor a 1900')
    .max(new Date().getFullYear() + 5, 'El año no puede ser mayor al año actual + 5'),
  firstAirDate: z.string().optional(),
  lastAirDate: z.string().optional(),
  totalSeasons: z.number().min(0).optional(),
  totalEpisodes: z.number().min(0).optional(),
  rating: z.number().optional(),
  actors: z.string().optional(),
  creators: z.string().optional(),
  spanishSynopsis: z.string().optional(),
  englishSynopsis: z.string().optional(),
  classification: z.string().nullable().transform((val) => val || null),
  genres: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  coverImageUrl: z.url().optional().or(z.literal('')),
  backgroundImageUrl: z.url().optional().or(z.literal('')),
  trailerUrl: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((value) => !value || isDigitalOceanSpacesUrl(value), {
      message: 'Por favor suba el trailer a Digital Ocean',
    }),
  reference: z.string().optional(),
  active: z.boolean(),
  adultContent: z.boolean(),
  isFeatured: z.boolean(),
  kidsContent: z.boolean()
});

type TVShowFormData = z.infer<ReturnType<typeof createTVShowSchema>>;

interface TVShowFormProps {
  onSuccess?: (data: CreateTVShowRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateTVShowRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const TVShowForm = ({
                      onSuccess,
                      onCancel,
                      initialData,
                      isEdit = false,
                      isSubmitting = false
                    }: TVShowFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [imdbSearchLoading, setImdbSearchLoading] = useState(false);
  const [imdbSearchError, setImdbSearchError] = useState<string | null>(null);
  const [detailsSource, setDetailsSource] = useState<ContentDetailsSource>(
    initialData?.tmdbId ? 'TMDB' : initialData?.imdbId ? 'IMDB' : 'TMDB',
  );
  const [showImdbOnly, setShowImdbOnly] = useState(!isEdit && !initialData?.title);
  const [selectedGenres, setSelectedGenres] = useState<string[]>(initialData?.genres || []);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categoryIds|| []);
  const [firstAirDateLoading, setFirstAirDateLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<TVShowFormData>({
    resolver: zodResolver(createTVShowSchema(t)),
    defaultValues: {
      title: initialData?.title || '',
      spanishTitle: initialData?.spanishTitle || '',
      originalTitle: initialData?.originalTitle || '',
      imdbId: initialData?.imdbId || '',
      tmdbId: initialData?.tmdbId || '',
      year: initialData?.year || new Date().getFullYear(),
      firstAirDate: initialData?.firstAirDate || '',
      lastAirDate: initialData?.lastAirDate || '',
      totalSeasons: initialData?.totalSeasons || 0,
      totalEpisodes: initialData?.totalEpisodes || 0,
      rating: initialData?.rating || 0,
      actors: initialData?.actors || '',
      creators: initialData?.creators || '',
      spanishSynopsis: initialData?.spanishSynopsis || '',
      englishSynopsis: initialData?.englishSynopsis || '',
      classification: initialData?.classification || '',
      genres: initialData?.genres || [],
      categoryIds: initialData?.categoryIds || [],
      coverImageUrl: initialData?.coverImageUrl || '',
      backgroundImageUrl: initialData?.backgroundImageUrl || '',
      trailerUrl: initialData?.trailerUrl || '',
      reference: initialData?.reference || '',
      active: initialData?.active ?? true,
      adultContent: initialData?.adultContent ?? false,
      isFeatured: initialData?.isFeatured ?? false,
      kidsContent: initialData?.kidsContent ?? false
    }
  });

  const searchDetails = async (tmdbIdOverride?: string) => {
    const imdbId = watch('imdbId') || '';
    const tmdbId = tmdbIdOverride || watch('tmdbId') || '';
    const lookupId = detailsSource === 'IMDB' ? imdbId : tmdbId;
    if (!lookupId.trim()) return;

    setImdbSearchLoading(true);
    setImdbSearchError(null);

    try {
      const tvShowData = await contentDetailsService.getTVShow({
        source: detailsSource,
        imdbId,
        tmdbId,
      });

      setValue('title', tvShowData.title);
      if (tvShowData.imdbId) setValue('imdbId', tvShowData.imdbId);
      if (tvShowData.tmdbId) setValue('tmdbId', tvShowData.tmdbId);
      if (tvShowData.year) setValue('year', tvShowData.year);
      if (tvShowData.firstAirDate) setValue('firstAirDate', tvShowData.firstAirDate);
      if (tvShowData.lastAirDate) setValue('lastAirDate', tvShowData.lastAirDate);
      setValue('totalSeasons', tvShowData.totalSeasons || 1);
      setValue('totalEpisodes', tvShowData.totalEpisodes || 1);
      setValue('actors', tvShowData.actors || '');
      setValue('creators', tvShowData.creators || '');
      setValue('englishSynopsis', tvShowData.englishSynopsis || '');
      if (tvShowData.spanishSynopsis) setValue('spanishSynopsis', tvShowData.spanishSynopsis);
      setValue('coverImageUrl', tvShowData.coverImageUrl || '');
      if (tvShowData.backgroundImageUrl) setValue('backgroundImageUrl', tvShowData.backgroundImageUrl);
      setValue('rating', tvShowData.rating ?? 0);
      if (tvShowData.spanishTitle) setValue('spanishTitle', tvShowData.spanishTitle);
      if (tvShowData.originalTitle) setValue('originalTitle', tvShowData.originalTitle);
      if (tvShowData.classification) setValue('classification', tvShowData.classification);

      if (tvShowData.genres && tvShowData.genres.length > 0) {
        const { genres: mappedGenres } = mapExternalTVShowGenres(tvShowData.genres, detailsSource);
        setSelectedGenres(mappedGenres);
        setValue('genres', mappedGenres);
      }

      setShowImdbOnly(false);
    } catch (error) {
      console.error('Content details search error:', error);
      setImdbSearchError(error instanceof Error ? error.message : `Error al buscar en ${detailsSource}`);
    } finally {
      setImdbSearchLoading(false);
      setFirstAirDateLoading(false);
    }
  };

  const handleImdbSearch = () => {
    void searchDetails();
  };

  const handleTmdbSelect = (tmdbId: string) => {
    setValue('tmdbId', tmdbId, { shouldDirty: true });
    if (tmdbId) {
      void searchDetails(tmdbId);
    }
  };

  const onSubmit = async (data: TVShowFormData) => {
    setSubmitError(null);

    try {
      await onSuccess?.(data as CreateTVShowRequest);
      if (!isEdit) {
        reset();
        setShowImdbOnly(true);
        setSelectedGenres([]);
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const { data: categoriesData } = useCategoriesForDropdown('TV_SHOW');

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  const imdbIdValue = watch('imdbId') || '';
  const tmdbIdValue = watch('tmdbId') || '';
  const titleValue = watch('title') || '';
  const yearValue = watch('year');
  const activeLookupId = detailsSource === 'IMDB' ? imdbIdValue : tmdbIdValue;
  const canSearchImdb = activeLookupId.trim().length > 0;
  const activeValue = watch('active');
  const adultContentValue = watch('adultContent');
  const isFeaturedValue = watch('isFeatured');
  const kidsContentValue = watch('kidsContent');
  const coverImageValue = watch('coverImageUrl');
  const backgroundImageValue = watch('backgroundImageUrl');
  const trailerUrlValue = watch('trailerUrl');

  const fillManually = () => {
    setShowImdbOnly(false);
    setImdbSearchError(null);
  };

  const handleBackgroundImageChange = (url: string) => {
    setValue('backgroundImageUrl', url);
  };

  const handleTrailerUrlChange = (url: string) => {
    setValue('trailerUrl', url);
  };

  if (showImdbOnly) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Crear Nueva Serie de TV
          </h2>
          <p className="text-gray-600 mt-1">
            Seleccione una fuente y busque el contenido para completar automáticamente los campos
          </p>
        </div>

        {imdbSearchError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{imdbSearchError}</p>
          </div>
        )}

        <div className="space-y-4">
          <Select
            label="Fuente"
            options={[
              { value: 'TMDB', label: 'TMDB' },
              { value: 'IMDB', label: 'IMDB' },
            ]}
            value={detailsSource}
            onChange={(event) => setDetailsSource(event.target.value as ContentDetailsSource)}
          />
          {detailsSource === 'IMDB' ? (
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Input
                  label="ID de IMDB"
                  placeholder="tt1234567 o 1234567"
                  helperText="Ingrese el ID de IMDB y presione Enter o haga clic en buscar"
                  {...register('imdbId')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleImdbSearch();
                    }
                  }}
                />
              </div>
              <Button
                type="button"
                onClick={handleImdbSearch}
                loading={imdbSearchLoading}
                disabled={imdbSearchLoading || !canSearchImdb}
              >
                {imdbSearchLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin"/>
                ) : (
                  <Search className="w-4 h-4"/>
                )}
                Buscar
              </Button>
            </div>
          ) : (
            <TmdbContentAutocomplete
              kind="tvShow"
              value={tmdbIdValue}
              selectedTitle={titleValue}
              selectedYear={yearValue}
              loading={imdbSearchLoading}
              onChange={handleTmdbSelect}
            />
          )}

          <div className="pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={fillManually}
              className="w-full"
            >
              Llenar formulario manualmente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Editar Serie de TV' : 'Crear Nueva Serie de TV'}
            </h2>
            <p className="text-gray-600">
              {isEdit ? 'Actualice la información de la serie' : 'Complete todos los campos requeridos'}
            </p>
          </div>
          {!isEdit && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowImdbOnly(true);
                reset();
                setSelectedGenres([]);
              }}
              className="flex items-center gap-2"
            >
              <Search className="w-4 h-4"/>
              Buscar detalles
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Top Section with Cover Image */}
        <div className="flex gap-6">
          {/* Left Side - Form Fields */}
          <div className="flex-1 space-y-4">
            <Input
              label="Título *"
              placeholder="Título de la serie"
              error={errors.title?.message}
              {...register('title')}
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Fuente de detalles
              </label>
              <Select
                options={[
                  { value: 'TMDB', label: 'TMDB' },
                  { value: 'IMDB', label: 'IMDB' },
                ]}
                value={detailsSource}
                onChange={(event) => setDetailsSource(event.target.value as ContentDetailsSource)}
              />
            </div>

            {detailsSource === 'IMDB' ? (
              <div className="space-y-1">
                <div className="flex rounded-md shadow-sm">
                  <input
                    placeholder="tt1234567"
                    value={imdbIdValue}
                    onChange={(event) => setValue('imdbId', event.target.value)}
                    className={`
                      flex-1 px-3 py-2 border border-gray-300 rounded-l-md rounded-r-none border-r-0
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                      disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                      ${errors.imdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                    `}
                  />
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="rounded-l-none border-l-0"
                    onClick={handleImdbSearch}
                    loading={imdbSearchLoading}
                    disabled={imdbSearchLoading || !canSearchImdb}
                  >
                    Actualizar
                  </Button>
                </div>
                {errors.imdbId?.message && (
                  <p className="text-sm text-red-600">{errors.imdbId.message}</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <TmdbContentAutocomplete
                    kind="tvShow"
                    value={tmdbIdValue}
                    selectedTitle={titleValue}
                    selectedYear={yearValue}
                    loading={imdbSearchLoading}
                    disabled={isSubmitting}
                    onChange={handleTmdbSelect}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:mt-6 sm:w-auto"
                  title="Actualizar detalles desde TMDB"
                  onClick={handleImdbSearch}
                  loading={imdbSearchLoading}
                  disabled={imdbSearchLoading || !canSearchImdb || isSubmitting}
                >
                  {!imdbSearchLoading && <RefreshCw className="mr-2 h-4 w-4" />}
                  Actualizar
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  ID de IMDB
                </label>
                <input
                  placeholder="tt1234567"
                  {...register('imdbId')}
                  className={`
                    w-full px-3 py-2 border border-gray-300 rounded-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${errors.imdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  `}
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  ID de TMDB
                </label>
                <input
                  placeholder="1399"
                  {...register('tmdbId')}
                  className={`
                    w-full px-3 py-2 border border-gray-300 rounded-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${errors.tmdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  `}
                />
              </div>
            </div>

            <ImageUrlUploadField
              label="Imagen Cover"
              placeholder="https://example.com/cover.jpg"
              value={coverImageValue || ''}
              onChange={(url) => setValue('coverImageUrl', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.coverImageUrl?.message}
              disabled={isSubmitting}
              source={MediaSource.TV_SHOW_POSTER}
              showPreview={false}
            />


          </div>

          {/* Right Side - Cover Image Preview */}
          <div className="flex-shrink-0">
            {coverImageValue ? (
              <img
                src={coverImageValue}
                alt="Vista previa de la portada"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                  target.style.display = 'block';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">de portada</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Título en español"
            placeholder="Título en español"
            error={errors.spanishTitle?.message}
            {...register('spanishTitle')}
          />

          <Input
            label="Título original"
            placeholder="Original Title"
            error={errors.originalTitle?.message}
            {...register('originalTitle')}/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Año *"
            type="number"
            placeholder="2024"
            error={errors.year?.message}
            {...register('year', { valueAsNumber: true })}
          />
          <Input
            label="Fecha Lanzamiento"
            type="date"
            error={errors.firstAirDate?.message}
            helperText={firstAirDateLoading ? 'Buscando fecha de lanzamiento en IMDB...' : undefined}
            helperLoading={firstAirDateLoading}
            disabled={firstAirDateLoading}
            {...register('firstAirDate')}
          />

          <Input
            label="Rating"
            placeholder="8.5/10"
            error={errors.rating?.message}
            {...register('rating', { valueAsNumber: true })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Actores
            </label>
            <textarea
              placeholder="Bryan Cranston, Aaron Paul, Anna Gunn..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('actors')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Creadores
            </label>
            <textarea
              placeholder="Vince Gilligan, David Shore..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('creators')}
            />
          </div>
        </div>

        {/* Synopsis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Español
            </label>
            <textarea
              placeholder="Sinopsis en español..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('spanishSynopsis')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Inglés
            </label>
            <textarea
              placeholder="Synopsis in English..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('englishSynopsis')}
            />
          </div>
        </div>

        {/* Status, Classification and Category */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Clasificación"
            options={tvShowClassificationOptions}
            placeholder="Seleccionar clasificación"
            error={errors.classification?.message}
            {...register('classification')}
          />

          <MultiSelect
            label="Categorías"
            placeholder="Selecciona categorías..."
            options={mapCategoriesToOptions(categoriesData || [])}
            value={selectedCategories}
            onChange={(newCategories) => {
              setSelectedCategories(newCategories);
              setValue('categoryIds', newCategories);
            }}
            disabled={isSubmitting}
            error={errors.categoryIds?.message}
          />
          <MultiSelect
            label="Géneros"
            placeholder="Selecciona los géneros..."
            options={tvShowGenreOptions}
            value={selectedGenres}
            onChange={(newGenres) => {
              setSelectedGenres(newGenres);
              setValue('genres', newGenres);
            }}
            disabled={isSubmitting}
          />
        </div>
        <MediaAssetsSection
          backgroundImageUrl={backgroundImageValue || ''}
          trailerUrl={trailerUrlValue || ''}
          onBackgroundImageChange={handleBackgroundImageChange}
          onTrailerUrlChange={handleTrailerUrlChange}
          backgroundImageSource={MediaSource.TV_SHOW_BACKGROUND}
          backgroundImageError={errors.backgroundImageUrl?.message}
          trailerUrlError={errors.trailerUrl?.message}
        />

        <Input
          label="Referencia"
          placeholder="Referencia adicional..."
          error={errors.reference?.message}
          {...register('reference')}
        />

        {/* Toggles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Toggle
            label="Activo"
            checked={activeValue}
            onChange={(checked) => setValue('active', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Para Adultos"
            checked={adultContentValue}
            onChange={(checked) => setValue('adultContent', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Para Niños"
            checked={kidsContentValue}
            onChange={(checked) => setValue('kidsContent', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Destacada"
            checked={isFeaturedValue}
            onChange={(checked) => setValue('isFeatured', checked)}
            disabled={isSubmitting}
          />
        </div>

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Guardando...'
              : isEdit
                ? 'Actualizar Serie'
                : 'Crear Serie'
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TVShowForm;
